import { createBookingApiClient } from "@openings/api-client";

function resolveBaseUrl(): string {
  const configuredBaseUrl = process.env.NEXT_PUBLIC_API_BASE;
  if (!configuredBaseUrl) {
    throw new Error(
      "NEXT_PUBLIC_API_BASE is not set. Configure it to point at the Fastify API origin (e.g. http://localhost:4000).",
    );
  }

  const normalized = configuredBaseUrl.replace(/\/$/, "");

  // In some local dev environments, Node's `fetch` resolves `localhost` to
  // IPv6 (`::1`) first, which can fail if the API is only bound on IPv4.
  // Prefer `127.0.0.1` server-side to reduce flakiness.
  if (typeof window === "undefined") {
    try {
      const url = new URL(normalized);
      if (url.hostname === "localhost") {
        url.hostname = "127.0.0.1";
        return url.toString().replace(/\/$/, "");
      }
    } catch {
      // If it's not a valid URL, fall back to the raw value.
    }
  }

  return normalized;
}

export const API_BASE_URL = resolveBaseUrl();

function shouldRetryFetch(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    typeof error.message === "string" &&
    error.message.includes("fetch failed")
  );
}

/**
 * Server-only hook for trusted-proxy rate-limit identity. All SSR fetches
 * leave through Vercel's shared egress IPs, so without this the API's per-IP
 * rate limits treat the entire platform's server-rendered traffic as a
 * handful of clients. A registered provider returns the trusted-proxy
 * headers (shared secret + real visitor IP) to merge into each request.
 *
 * Registration happens in `trustedProxyServer.ts` (imported by the root
 * layout), which keeps the `next/headers` dependency out of this module —
 * client components also import `apiClient`, and in the browser bundle the
 * provider simply stays null.
 */
type TrustedProxyHeaderProvider = () => Promise<Record<string, string> | null>;

let trustedProxyHeaderProvider: TrustedProxyHeaderProvider | null = null;

export function registerTrustedProxyHeaderProvider(
  provider: TrustedProxyHeaderProvider,
): void {
  trustedProxyHeaderProvider = provider;
}

/** Shared server fetch: no-store, trusted-proxy headers, one retry. */
export const noStoreFetch: typeof fetch = (input, init) => {
  const fetchImpl = globalThis.fetch;
  if (typeof fetchImpl !== "function") {
    throw new Error("Fetch API is not available in this environment");
  }

  const attempt = async () => {
    const trustedHeaders = trustedProxyHeaderProvider
      ? await trustedProxyHeaderProvider()
      : null;
    const requestInit: RequestInit = {
      ...(init ?? {}),
      cache: "no-store",
    };
    if (trustedHeaders) {
      const merged = new Headers(init?.headers);
      for (const [key, value] of Object.entries(trustedHeaders)) {
        merged.set(key, value);
      }
      requestInit.headers = merged;
    }
    return fetchImpl(input as any, requestInit);
  };

  return attempt().catch(async (error) => {
    if (!shouldRetryFetch(error)) {
      throw error;
    }
    return attempt();
  });
};

export const apiClient = createBookingApiClient(API_BASE_URL, {
  fetchImpl: noStoreFetch,
});

export type {
  ApiUser,
  ApiError,
  NextOpeningsItem,
  OpeningsItem,
  LocationOpeningsItem,
  GetOpeningsResult,
  GetNextOpeningsResult,
  GetLocationOpeningsResult,
  GetLocationUserDetailsResult,
  SalonDTO,
  SalonGetResult,
  ServiceDTO,
  ServiceOptionDTO,
  AppointmentDTO,
  CreateAppointmentInput,
  RescheduleAppointmentInput,
  CancelAppointmentInput,
  AppointmentHistoryResult,
  FeedbackInput,
  FeedbackResult,
  PaymentMethod,
  CustomerAppointmentSummary,
  ListCustomerAppointmentsResult,
  LocationsListResult,
  LocationDetailResult,
  LocationDTO,
  LocationMemberDTO,
  LocationServiceDTO,
  GetUserPageResult,
  UserScheduleDTO,
  UserServiceDTO,
  UserVacationDTO,
  UserPhotoDTO,
  UserPageDTO,
  PhotoItem,
  PhotosListData,
} from "@openings/api-client";

// Canonical schedule-based type re-exports
export type {
  SchedulePublic,
  ScheduleMember,
  ScheduleService,
  ScheduleDetailResponse,
  SchedulesListResponse,
  MemberDetailsResponse,
} from "@openings/api-contract";
